/**
 * Preview Service
 *
 * API service for teacher content preview endpoints.
 * All endpoints require TEACHER/PRINCIPAL/ADMIN role.
 */

import api from "./api"; // Your configured Axios instance

// ============================================================================
// Types
// ============================================================================

export interface PreviewProgramSummary {
  programId: string;
  programName: string;
  totalWeeks: number;
  cefrLevel: string;
  description: string;
  modulesPerWeek: number;
}

export interface PreviewProgramDetail {
  programId: string;
  programName: string;
  cefrLevel: string;
  description: string;
  totalWeeks: number;
  modulesPerWeek: number;
  weeks: PreviewWeekSummary[];
}

export interface PreviewWeekSummary {
  weekNumber: number;
  weekId: string;
  weekName: string;
  modules: PreviewModuleSummary[];
  totalModules: number;
}

export interface PreviewModuleSummary {
  moduleId: string;
  moduleType: string;
  moduleName: string;
  description: string;
  estimatedTime: number;
  questionCount: number;
  hasLearningContent?: boolean;
  hasQuiz?: boolean;
  sections?: any[];
  learningObjectives?: string[];
}

export interface PreviewWeekDetail {
  weekNumber: number;
  weekId: string;
  weekName: string;
  programName: string;
  modules: PreviewModuleSummary[];
}

export interface PreviewModuleContent {
  moduleId: string;
  moduleType: string;
  moduleName: string;
  weekNumber: number;
  weekId: string;
  programName: string;
  description: string;
  estimatedTime: number;
  learningObjectives: string[];
  learningContent: {
    sections: any[];
    content: string;
  };
  quiz: {
    questionCount: number;
    totalPoints: number;
    questions: PreviewQuestion[];
  };
}

export interface PreviewQuestion {
  questionId: string;
  id?: string;
  questionType: string;
  questionText: string;
  points: number;
  difficulty?: string;
  skill?: string;
  module?: string;
  week?: number;
  program?: string;

  // Type 2, 3: Single MCQ
  options?: string[];
  correctAnswer?: number | string | string[];
  correctAnswerText?: string;
  acceptableAnswers?: string[];
  explanation?: string;
  hint?: string;

  // Type 1, 6, 10: Sub-questions array
  questions?: Array<{
    index?: number;
    question: string;
    options?: string[];
    correctAnswer?: number;
    correctAnswerText?: string;
    correctOption?: string; // Text of correct answer (alternative to index)
    answer?: string;
    acceptableAnswers?: string[];
  }>;
  totalSubQuestions?: number;

  // Type 4: Sentence ordering
  words?: string[];
  correctOrder?: number[];
  correctSentence?: string;

  // Type 8: Word matching
  wordPairs?: Array<{
    word: string;
    meaning: string;
    context?: string;
    phonetic?: string;
  }>;
  leftColumn?: string[];
  rightColumn?: string[];

  // Type 9: Drag-drop matching
  items?: string[];
  categories?: string[];
  correctMatches?: Array<{
    left?: string;
    right?: string;
    item?: string;
    category?: string;
  }>;

  // Type 11: Speaking practice
  sentences?: string[];
  script?: string;
  modelAudio?: string;

  // Type 12: Flip card content
  content?: {
    questionTitle?: string;
    answerTitle?: string;
    questionContent?: Array<{ text: string; isBold?: boolean }>;
    answerContent?: Array<{ text: string; isBold?: boolean }>;
  };

  // Type 13: Highlighting
  text?: string;
  correctHighlights?: string[];

  // Media assets
  asset?: string;
  questionImage?: {
    url: string;
    filename?: string;
    contentType?: string;
  };
  questionAudio?: {
    url: string;
    filename?: string;
    contentType?: string;
  };
  audio?: string;
}

// API Response wrapper (matches your backend pattern)
interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

// ============================================================================
// Preview Service
// ============================================================================

export const previewService = {
  /**
   * Get all available programs for preview
   * GET /api/preview/programs
   */
  async getPrograms(): Promise<PreviewProgramSummary[]> {
    try {
      const response = await api.get<ApiResponse<PreviewProgramSummary[]>>(
        "/preview/programs"
      );
      return response.data.data;
    } catch (error) {
      console.error("Failed to fetch preview programs:", error);
      throw error;
    }
  },

  /**
   * Get all weeks for a specific program
   * GET /api/preview/programs/:programName
   *
   * @param programName - "yellow" | "green" | "access"
   */
  async getProgramWeeks(programName: string): Promise<PreviewProgramDetail> {
    try {
      const response = await api.get<ApiResponse<PreviewProgramDetail>>(
        `/preview/programs/${programName.toLowerCase()}`
      );
      return response.data.data;
    } catch (error) {
      console.error(`Failed to fetch weeks for program ${programName}:`, error);
      throw error;
    }
  },

  /**
   * Get detailed module information for a specific week
   * GET /api/preview/programs/:programName/week/:weekNumber
   *
   * @param programName - "yellow" | "green" | "access"
   * @param weekNumber - Week number (1-20 for yellow/green, 1-12 for access)
   */
  async getWeekModules(
    programName: string,
    weekNumber: number
  ): Promise<PreviewWeekDetail> {
    try {
      const response = await api.get<ApiResponse<PreviewWeekDetail>>(
        `/preview/programs/${programName.toLowerCase()}/week/${weekNumber}`
      );
      return response.data.data;
    } catch (error) {
      console.error(
        `Failed to fetch modules for ${programName} week ${weekNumber}:`,
        error
      );
      throw error;
    }
  },

  /**
   * Get full module content including learning content and quiz WITH ANSWERS
   * GET /api/preview/programs/:programName/week/:weekNumber/module/:moduleType
   *
   * @param programName - "yellow" | "green" | "access"
   * @param weekNumber - Week number
   * @param moduleType - "reading" | "writing" | "listening" | "speaking" | "grammar" | "vocabulary" | "review"
   */
  async getModuleContent(
    programName: string,
    weekNumber: number,
    moduleType: string
  ): Promise<PreviewModuleContent> {
    try {
      const response = await api.get<ApiResponse<PreviewModuleContent>>(
        `/preview/programs/${programName.toLowerCase()}/week/${weekNumber}/module/${moduleType.toLowerCase()}`
      );
      return response.data.data;
    } catch (error) {
      console.error(
        `Failed to fetch module content for ${programName} week ${weekNumber} ${moduleType}:`,
        error
      );
      throw error;
    }
  },

  /**
   * Get module content by direct module ID
   * GET /api/preview/module/:moduleId
   *
   * @param moduleId - e.g., "yellow-week1-reading"
   */
  async getModuleById(moduleId: string): Promise<PreviewModuleContent> {
    try {
      const response = await api.get<ApiResponse<PreviewModuleContent>>(
        `/preview/module/${moduleId}`
      );
      return response.data.data;
    } catch (error) {
      console.error(`Failed to fetch module ${moduleId}:`, error);
      throw error;
    }
  },
};

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get display name for program
 */
export const getProgramDisplayName = (programId: string): string => {
  const names: Record<string, string> = {
    yellow: "EAP Yellow",
    green: "EAP Green",
    access: "EAP Access",
  };
  return names[programId.toLowerCase()] || programId;
};

/**
 * Get CEFR level for program
 */
export const getProgramCefrLevel = (programId: string): string => {
  const levels: Record<string, string> = {
    yellow: "CEFR B1",
    green: "CEFR B1-B2",
    access: "CEFR B2-C1",
  };
  return levels[programId.toLowerCase()] || "";
};

/**
 * Get total weeks for program
 */
export const getProgramTotalWeeks = (programId: string): number => {
  const weeks: Record<string, number> = {
    yellow: 20,
    green: 20,
    access: 12,
  };
  return weeks[programId.toLowerCase()] || 20;
};

/**
 * Get icon name for module type (for use with MUI icons)
 */
export const getModuleIconName = (moduleType: string): string => {
  const icons: Record<string, string> = {
    reading: "MenuBook",
    writing: "Create",
    listening: "Headset",
    speaking: "RecordVoiceOver",
    grammar: "Gavel",
    vocabulary: "Translate",
    review: "AssignmentTurnedIn",
  };
  return icons[moduleType.toLowerCase()] || "School";
};

/**
 * Get color for module type (for UI styling)
 */
export const getModuleColor = (moduleType: string): string => {
  const colors: Record<string, string> = {
    reading: "#4CAF50", // Green
    writing: "#2196F3", // Blue
    listening: "#9C27B0", // Purple
    speaking: "#FF9800", // Orange
    grammar: "#F44336", // Red
    vocabulary: "#00BCD4", // Cyan
    review: "#607D8B", // Blue Grey
  };
  return colors[moduleType.toLowerCase()] || "#757575";
};

export default previewService;
